import { createContext, useCallback, useContext, useEffect, useReducer, useState } from 'react'

import { getUrlAuthTokens } from '../../util/url-auth-tokens'

import { cleanAuthenticationCookie, getCookie, setAuthenticationCookie } from './authentication.helpers'
import { useAuthenticationMethod } from './useAuthenticationMethod'

import type { AuthenticationParams } from './authentication.helpers'

type AuthenticationContextType = {
  isAuthenticated: boolean
  logout: () => void
  handleSuccessfulLogin: (params: AuthenticationParams) => void
  isLoading: boolean
}

const AuthenticationContext = createContext<AuthenticationContextType>({
  isAuthenticated: false,
  logout: () => void 0,
  handleSuccessfulLogin: () => void 0,
  isLoading: true,
})

type AuthenticationProviderProps = {
  children: React.ReactNode
}

export const AuthenticationProvider = ({ children }: AuthenticationProviderProps) => {
  const [isLoading, toggleIsLoading] = useReducer(() => false, true)
  const [isAuthenticated, setIsAuthenticated] = useState(false)

  const { connectWithImpersonateToken, connectWithMagicToken, connectWithPaypal } = useAuthenticationMethod({
    setIsAuthenticated,
    toggleIsLoading,
  })

  const logout = useCallback(() => {
    setIsAuthenticated(false)
    cleanAuthenticationCookie()
  }, [])

  const handleSuccessfulLogin = useCallback((authData: AuthenticationParams) => {
    setAuthenticationCookie(authData)
    setIsAuthenticated(true)
  }, [])

  useEffect(() => {
    const { impersonateToken, magicToken, paypalSsoToken, idToken } = getUrlAuthTokens()
    if (magicToken) {
      connectWithMagicToken(magicToken)
      return
    }

    if (impersonateToken) {
      connectWithImpersonateToken(impersonateToken)
      return
    }

    if (paypalSsoToken && idToken) {
      connectWithPaypal(idToken)
      return
    }

    const token = getCookie('token')
    if (token) {
      setIsAuthenticated(true)
    }

    toggleIsLoading()
  }, [])

  const value: AuthenticationContextType = {
    isAuthenticated,
    logout,
    handleSuccessfulLogin,
    isLoading,
  }

  return <AuthenticationContext.Provider value={value}>{children}</AuthenticationContext.Provider>
}

export const useAuthenticationProvider = () => useContext(AuthenticationContext)
